import type {
  AppData,
  CompRef,
  DocumentServiceError,
  DocumentServicesObject,
} from '@wix/document-services-types';
import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { TPAMessage, TPAMessageCallback } from '../types/tpaMessage';
import type { TpaHelpService } from './tpaHelpService';
import { createTpaUtils } from '../utils/tpaUtils';

interface GetDashboardBaseUrlOptions {
  deviceType: string;
  state: string;
  origin: string;
  widgetId: string;
}

interface TpaDashboardServiceDependencies {
  tpaHelpService: TpaHelpService;
}

export const createTpaDashboardService = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  { tpaHelpService }: TpaDashboardServiceDependencies,
) => {
  const tpaUtils = createTpaUtils(platformApiMethods, documentServicesAPI);

  const getSelectedComponentData = async (selectedComponent?: CompRef) => {
    const selectedComp =
      selectedComponent ??
      (await platformApiMethods.editor.selection.getSelectedComponents())[0];
    return documentServicesAPI.components.data.get(selectedComp);
  };

  const getDashboardUrl = async (
    appData: AppData,
    selectedComponent: CompRef,
    panelCompId: string,
    params: GetDashboardBaseUrlOptions,
  ) => {
    const msid = documentServicesAPI.generalInfo.getMetaSiteId();
    const bizManagerUrl = tpaUtils.getBusinessManagerUrl();
    const bizManagerBaseUrl = bizManagerUrl?.replace(msid, '');
    const baseUrl = `${bizManagerBaseUrl}/external?appDefId=${appData.appDefinitionId}&shouldHideEditorHeader=true`;

    const urlParams = {
      url: baseUrl,
      deviceType: params?.deviceType,
      applicationId: appData.applicationId,
      origCompId: selectedComponent.id,
      origin: params?.origin,
      hash: params?.state,
    };

    return documentServicesAPI.tpa.app.url.getSettingsModalUrl(
      urlParams,
      panelCompId,
    );
  };

  const openDashboard = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const msgData = msg.data ?? {};
    const hashToSet = msgData.state || '';
    const origin = msgData.origin || '';
    const deviceType = msg.deviceType || 'desktop';

    const [selectedComponent] =
      await platformApiMethods.editor.selection.getSelectedComponents();

    const origCompData = await getSelectedComponentData(selectedComponent);
    if (!origCompData) {
      return;
    }

    const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
      origCompData.appDefinitionId,
    );

    if (!appData?.appDefinitionId) {
      return;
    }

    const { widgetId } = origCompData;

    const dashboardParams = {
      state: hashToSet,
      origin,
      widgetId,
      deviceType,
    };
    const dashboardPanelCompId = msg.compId;
    const url = await getDashboardUrl(
      appData,
      selectedComponent,
      dashboardPanelCompId,
      dashboardParams,
    );

    const helpId = await tpaHelpService.getHelpId(
      'settings_modal',
      deviceType,
      selectedComponent,
      appData.appDefinitionId,
      widgetId,
    );

    const options = {
      url,
      closeOtherPanels: false,
      shouldNotCloseOnBlur: true,
      leavePanelsOpen: true,
      shouldOverrideUrl: true,
      compId: msg.compId,
      appDefinitionId: appData.appDefinitionId,
      helpId,
      onCloseCallback: callback,
      onHelp: () =>
        platformApiMethods.editor.openHelpPanel({} as AppData, '', {
          helpId,
        }),
    };

    const isDraftMode = documentServicesAPI.generalInfo.isDraft();

    if (!appData.notProvisioned && !appData.demoMode && !isDraftMode) {
      platformApiMethods.editor.openDashboardPanel(appData, '', options);
      return;
    }

    const onSuccess = () => {
      // TODO - report success
      platformApiMethods.editor.openDashboardPanel(appData, '', options);
    };

    const onError = (error: DocumentServiceError) => {
      platformApiMethods.editor.showSavePublishFailAlert?.(appData, '', {
        error,
      });
    };

    platformApiMethods.editor
      .saveInBackground('openDashboard', {})
      .then(onSuccess, onError);
  };

  return { openDashboard };
};
